/**
 * Paramètres de l'espace (CDC §8.1).
 *
 * L'écran d'installation cochait des étapes sans rien configurer. C'est ici que
 * les réglages s'enregistrent réellement.
 *
 * Les valeurs par défaut du TERRAIN sont affichées mais NON modifiables : elles
 * dépendent des arbitrages A10 (base juridique de la géolocalisation) et A12.
 * Les rendre modifiables laisserait activer une collecte de données de salariés
 * sans base légale établie. Elles sont montrées quand même : cacher un réglage
 * actif serait pire que l'afficher grisé.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type Settings } from '../api/client';
import { Champ, ErreurFormulaire, Ligne } from '../composants/Formulaire';
import { LogosEntreprise } from '../composants/LogosEntreprise';
import { signalerIdentiteModifiee } from '../domaine/identite-espace';
import { useInstallation } from '../domaine/installation-terminee';

const DEVISES = ['EUR', 'CHF', 'GBP', 'USD', 'CAD', 'MAD', 'TND', 'XOF'];

const MOIS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

interface Saisie {
  legalName: string;
  legalIdentifier: string;
  vatNumber: string;
  line1: string;
  line2: string;
  postalCode: string;
  city: string;
  countryCode: string;
  phone: string;
  email: string;
  website: string;
  primaryColor: string;
  accentColor: string;
  customDomain: string;
  currency: string;
  fiscalYearStartMonth: number;
  planningHorizonMonths: number;
}

function depuis(s: Settings): Saisie {
  return {
    legalName: s.legalName ?? '',
    legalIdentifier: s.legalIdentifier ?? '',
    vatNumber: s.vatNumber ?? '',
    line1: s.address?.line1 ?? '',
    line2: s.address?.line2 ?? '',
    postalCode: s.address?.postalCode ?? '',
    city: s.address?.city ?? '',
    countryCode: s.address?.countryCode ?? '',
    phone: s.phone ?? '',
    email: s.email ?? '',
    website: s.website ?? '',
    primaryColor: s.primaryColor ?? '',
    accentColor: s.accentColor ?? '',
    customDomain: s.customDomain ?? '',
    currency: s.currency,
    fiscalYearStartMonth: s.fiscalYearStartMonth,
    planningHorizonMonths: s.planningHorizonMonths,
  };
}

export function SettingsPage() {
  const installation = useInstallation();
  const [reglages, setReglages] = useState<Settings | null>(null);
  const [saisie, setSaisie] = useState<Saisie | null>(null);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    try {
      const s = await api.settings();
      setReglages(s);
      setSaisie(depuis(s));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Chargement impossible.'));
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const modifier = <K extends keyof Saisie>(cle: K, valeur: Saisie[K]) =>
    setSaisie((s) => (s ? { ...s, [cle]: valeur } : s));

  const enregistrer = async (e: FormEvent) => {
    e.preventDefault();
    if (!saisie) return;

    setEnCours(true);
    setErreur(null);
    setSucces(null);
    try {
      // Une adresse entièrement vide est envoyée en `null` : garder un objet de
      // champs nuls ferait croire à une adresse renseignée.
      const adresse = [saisie.line1, saisie.line2, saisie.postalCode, saisie.city, saisie.countryCode]
        .some((v) => v.trim() !== '')
        ? {
            line1: vide(saisie.line1),
            line2: vide(saisie.line2),
            postalCode: vide(saisie.postalCode),
            city: vide(saisie.city),
            countryCode: vide(saisie.countryCode),
          }
        : null;

      const resultat = await api.updateSettings({
        legalName: vide(saisie.legalName),
        legalIdentifier: vide(saisie.legalIdentifier),
        vatNumber: vide(saisie.vatNumber),
        address: adresse,
        phone: vide(saisie.phone),
        email: vide(saisie.email),
        website: vide(saisie.website),
        primaryColor: vide(saisie.primaryColor),
        accentColor: vide(saisie.accentColor),
        customDomain: vide(saisie.customDomain),
        currency: saisie.currency,
        fiscalYearStartMonth: saisie.fiscalYearStartMonth,
        planningHorizonMonths: saisie.planningHorizonMonths,
      });

      setReglages(resultat);
      setSaisie(depuis(resultat));
      setSucces('Paramètres enregistrés.');
      // La raison sociale est aussi le nom affiché en tête du menu.
      signalerIdentiteModifiee();
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Enregistrement impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  if (!saisie || !reglages) {
    return erreur ? (
      <ErreurFormulaire message={erreur.message} />
    ) : (
      <div className="vide">Chargement…</div>
    );
  }

  return (
    <>
      <div className="entete-page">
        <div>
          <h1>Paramètres de l’espace</h1>
          <p>Identité, apparence et règles d’exploitation. Ces valeurs servent aux documents.</p>
        </div>
        <button onClick={(e) => void enregistrer(e)} disabled={enCours}>
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      <ErreurFormulaire
        message={erreur?.message ?? null}
        details={erreur instanceof ApiError ? erreur.details : undefined}
      />
      {succes && <div className="message succes">{succes}</div>}

      <form onSubmit={(e) => void enregistrer(e)}>
        <div className="carte">
          <h2>Identité de l’entreprise</h2>
          <div className="carte-corps">
            <Champ libelle="Raison sociale" aide="Telle qu’elle figurera sur vos documents.">
              <input
                type="text"
                value={saisie.legalName}
                onChange={(e) => modifier('legalName', e.target.value)}
              />
            </Champ>

            <Ligne>
              <Champ libelle="SIRET / identifiant légal">
                <input
                  type="text"
                  value={saisie.legalIdentifier}
                  onChange={(e) => modifier('legalIdentifier', e.target.value)}
                />
              </Champ>
              <Champ libelle="N° de TVA intracommunautaire">
                <input
                  type="text"
                  value={saisie.vatNumber}
                  onChange={(e) => modifier('vatNumber', e.target.value)}
                />
              </Champ>
            </Ligne>

            <Champ libelle="Adresse">
              <input
                type="text"
                placeholder="Numéro et voie"
                value={saisie.line1}
                onChange={(e) => modifier('line1', e.target.value)}
              />
            </Champ>
            <Champ libelle="Complément d’adresse">
              <input
                type="text"
                value={saisie.line2}
                onChange={(e) => modifier('line2', e.target.value)}
              />
            </Champ>

            <Ligne>
              <Champ libelle="Code postal">
                <input
                  type="text"
                  value={saisie.postalCode}
                  onChange={(e) => modifier('postalCode', e.target.value)}
                />
              </Champ>
              <Champ libelle="Ville">
                <input
                  type="text"
                  value={saisie.city}
                  onChange={(e) => modifier('city', e.target.value)}
                />
              </Champ>
              <Champ libelle="Pays" aide="Code à deux lettres : FR, CH, BE…">
                <input
                  type="text"
                  maxLength={2}
                  value={saisie.countryCode}
                  onChange={(e) => modifier('countryCode', e.target.value.toUpperCase())}
                />
              </Champ>
            </Ligne>

            <Ligne>
              <Champ libelle="Téléphone" aide="Imprimé en tête du bon d’intervention.">
                <input
                  type="text"
                  inputMode="tel"
                  maxLength={40}
                  placeholder="04 79 00 00 00"
                  value={saisie.phone}
                  onChange={(e) => modifier('phone', e.target.value)}
                />
              </Champ>
              <Champ libelle="Adresse électronique">
                <input
                  type="email"
                  maxLength={200}
                  placeholder="contact@exemple.fr"
                  value={saisie.email}
                  onChange={(e) => modifier('email', e.target.value)}
                />
              </Champ>
              <Champ libelle="Site web">
                <input
                  type="text"
                  maxLength={200}
                  placeholder="www.exemple.fr"
                  value={saisie.website}
                  onChange={(e) => modifier('website', e.target.value)}
                />
              </Champ>
            </Ligne>
          </div>
        </div>

        <LogosEntreprise reglages={reglages} onChange={setReglages} />

        <div className="carte">
          <h2>Apparence</h2>
          <div className="carte-corps">
            <Ligne>
              <Champ libelle="Couleur principale" aide="Format #rrggbb, par exemple #1c4e80.">
                <div className="saisie-couleur">
                  <input
                    type="color"
                    value={saisie.primaryColor || '#1c4e80'}
                    onChange={(e) => modifier('primaryColor', e.target.value)}
                    aria-label="Choisir la couleur principale"
                  />
                  <input
                    type="text"
                    placeholder="#1c4e80"
                    value={saisie.primaryColor}
                    onChange={(e) => modifier('primaryColor', e.target.value)}
                  />
                </div>
              </Champ>
              <Champ libelle="Couleur d’accent">
                <div className="saisie-couleur">
                  <input
                    type="color"
                    value={saisie.accentColor || '#b46a00'}
                    onChange={(e) => modifier('accentColor', e.target.value)}
                    aria-label="Choisir la couleur d’accent"
                  />
                  <input
                    type="text"
                    placeholder="#b46a00"
                    value={saisie.accentColor}
                    onChange={(e) => modifier('accentColor', e.target.value)}
                  />
                </div>
              </Champ>
            </Ligne>

            <Champ
              libelle="Domaine personnalisé"
              aide="Sans https:// ni chemin. Le raccordement DNS reste à faire séparément."
            >
              <input
                type="text"
                placeholder="airindu.exemple.fr"
                value={saisie.customDomain}
                onChange={(e) => modifier('customDomain', e.target.value)}
              />
            </Champ>

            <div className="message info">
              Les couleurs sont enregistrées mais ne repeignent pas encore l’interface.
            </div>
          </div>
        </div>

        <div className="carte">
          <h2>Exploitation</h2>
          <div className="carte-corps">
            <Ligne>
              <Champ libelle="Devise">
                <select
                  value={saisie.currency}
                  onChange={(e) => modifier('currency', e.target.value)}
                >
                  {DEVISES.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </Champ>
              <Champ libelle="Début d’exercice comptable">
                <select
                  value={saisie.fiscalYearStartMonth}
                  onChange={(e) => modifier('fiscalYearStartMonth', Number(e.target.value))}
                >
                  {MOIS.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
              </Champ>
              <Champ
                libelle="Horizon de planification"
                aide="En mois. Sert à générer les échéances de maintenance."
              >
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={saisie.planningHorizonMonths}
                  onChange={(e) => modifier('planningHorizonMonths', Number(e.target.value))}
                />
              </Champ>
            </Ligne>
          </div>
        </div>

        <div className="carte">
          <h2>Terrain — valeurs par défaut</h2>
          <div className="carte-corps">
            <div className="message info">
              Ces réglages sont <strong>en lecture seule</strong>. La géolocalisation attend une base
              juridique écrite (arbitrage A10) : l’activer sans elle exposerait vos salariés et vous.
            </div>
            <dl className="proprietes">
              <dt>Verrouillage de l’écran</dt>
              <dd>{reglages.fieldDefaults.screenLockMinutes} minutes</dd>
              <dt>Fin de session</dt>
              <dd>{reglages.fieldDefaults.sessionEndHour} h</dd>
              <dt>Autonomie hors ligne</dt>
              <dd>{reglages.fieldDefaults.offlineMaxDays} jours</dd>
              <dt>Géolocalisation</dt>
              <dd>
                <span
                  className={`etat ${reglages.fieldDefaults.geolocationEnabled ? 'actif' : 'suspendu'}`}
                >
                  {reglages.fieldDefaults.geolocationEnabled ? 'Activée' : 'Désactivée'}
                </span>
              </dd>
              <dt>Conservation des positions</dt>
              <dd>{reglages.fieldDefaults.geolocationRetentionDays} jours</dd>
            </dl>
          </div>
        </div>
      </form>

      {/* L'installation quitte le menu une fois terminée, mais reste consultable :
          on peut vouloir revoir ce qui a été fait ou ignoré. */}
      {installation === 'terminee' && (
        <p className="aide">
          <Link to="/installation">Revoir les étapes d’installation</Link>
        </p>
      )}
    </>
  );
}

function vide(v: string): string | null {
  return v.trim() === '' ? null : v.trim();
}
